import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  structuredData,
  altTitleKey,
  altDescriptionKey
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  // Use translation keys if provided, otherwise use direct text
  const seoTitle = altTitleKey ? t(altTitleKey) : title;
  const seoDescription = altDescriptionKey ? t(altDescriptionKey) : description;
  
  const baseTitle = "FotografX";
  const fullTitle = seoTitle ? `${seoTitle} | ${baseTitle}` : baseTitle;
  
  const defaultDescription = currentLang === 'de' 
    ? "Professionelle Fotografie in Gera und Ronneburg - Hochzeiten, Portraits und Events. FotografX bietet hochwertige Fotografie-Services mit künstlerischem Anspruch."
    : "Professional photography in Gera and Ronneburg - weddings, portraits and events. FotografX offers high-quality photography services with artistic excellence.";
  
  const finalDescription = seoDescription || defaultDescription;
  
  const defaultKeywords = currentLang === 'de'
    ? "Fotograf Berlin, Hochzeitsfotograf, Portraitfotograf, Architekturfotografie, Event Fotografie, professionelle Fotografie"
    : "photographer Berlin, wedding photographer, portrait photographer, architecture photography, event photography, professional photography";
  
  const finalKeywords = keywords || defaultKeywords;
  
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://fotografx.de';
  const finalUrl = url ? `${siteUrl}${url}` : siteUrl;
  const finalImage = image ? `${siteUrl}${image}` : `${siteUrl}/og-image.jpg`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content="FotografX" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content={currentLang} />
      <link rel="canonical" href={finalUrl} />
      
      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:site_name" content="FotografX" />
      <meta property="og:locale" content={currentLang === 'de' ? 'de_DE' : 'ru_RU'} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      
      {/* Additional SEO Tags */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Hreflang for multilingual support */}
      {currentLang === 'de' && (
        <>
          <link rel="alternate" hrefLang="de" href={finalUrl} />
          <link rel="alternate" hrefLang="ru" href={finalUrl.replace('/de/', '/ru/')} />
          <link rel="alternate" hrefLang="x-default" href={finalUrl} />
        </>
      )}
    </Helmet>
  );
};

export default SEO;